import React from "react";
import AcademicBackground from "./Components/AcademicBackground";
import ResearchTools from "./Components/ResearchTools";
import Chat from "./Components/Chat";
import Footer from "./Components/Footer";
import ScienceArt from "./Components/ScienceArt";
import portrait from "./images/portrait.webp";
import botanical from "./images/illustrations/botanical.webp";
import cvPdf from "./docs/cv.pdf";
import "./styles.css";

const faculty = "https://www.agri.sjtu.edu.cn/faculty/46001";
const email = "sjtu15364534609@sjtu.edu.cn";
const scholar = "https://scholar.google.com.hk/citations?user=tFTwrVYAAAAJ&hl";
const projects = [
  {
    title: "铁碗计划",
    summary: "构建细胞工厂，探索高价值产品的可持续、高效生物制造。",
    tags: ["Biomanufacturing", "Synthetic Biology"],
    kind: "cell",
  },
  {
    title: "WORF Seq",
    summary: "探索低成本、高通量的捕获测序技术。",
    tags: ["Sequencing", "CRISPR / Cas"],
    kind: "dna",
  },
];
const publications = [
  {
    title:
      "A promoter polymorphism defines distinct roles in anther development for Col-0 and Ler-0 alleles of Arabidopsis ACYL-COA BINDING PROTEIN3",
    venue: "New Phytologist",
    year: "2024",
    url: "https://nph.onlinelibrary.wiley.com/doi/10.1111/nph.19924",
  },
  {
    title: "Interactions between plant lipid-binding proteins and their ligands",
    venue: "Progress in Lipid Research",
    year: "2022",
    url: "https://www.sciencedirect.com/science/article/abs/pii/S016378272200011X",
  },
  {
    title:
      "Crystal structure of the rice acyl-CoA-binding protein OsACBP2 in complex with C18:3-CoA reveals a novel pattern of binding to acyl-CoA esters",
    venue: "FEBS Letters",
    year: "2020",
    linkLabel: "Scholar",
  },
  {
    title:
      "The overexpression of rice ACYL-CoA-BINDING PROTEIN 2 increases grain size and bran oil content in transgenic rice",
    venue: "The Plant Journal",
    year: "2019",
    linkLabel: "Scholar",
  },
];

export default function App() {
  return (
    <div className="site-shell">
      <a className="skip-link" href="#main">
        跳至正文
      </a>
      <header className="site-header content-width">
        <a className="brand" href="#home" aria-label="郭泽华，返回首页">
          <span className="monogram" aria-hidden="true">
            Z<sub>G</sub>
          </span>
          <span className="brand-name">
            郭泽华 <span lang="en">Zehua Guo</span>
          </span>
        </a>
        <nav aria-label="主导航">
          <a href="#background">学术经历</a>
          <a href="#research">研究方向</a>
          <a href="#publications">代表论文</a>
          <a href="#contact">联系方式</a>
        </nav>
      </header>
      <main id="main" className="content-width">
        <section id="home" className="hero" aria-labelledby="hero-name">
          <div className="hero-copy">
            <p className="eyebrow" lang="en">
              Synthetic Biology <span aria-hidden="true">×</span> AI
            </p>
            <div className="hero-identity">
              <h1 id="hero-name">
                郭泽华<span lang="en">Zehua Guo</span>
              </h1>
              <img
                className="hero-portrait"
                src={portrait}
                alt="郭泽华个人照片"
                width="118"
                height="148"
              />
            </div>
            <p className="affiliation">
              <a
                href={faculty}
                target="_blank"
                rel="noreferrer"
                aria-label="查看上海交通大学官方教师主页"
              >
                上海交通大学 · 农业与生物学院 · 助理研究员 <span aria-hidden="true">↗</span>
              </a>
            </p>
            <span className="short-rule" aria-hidden="true" />
            <h2 className="hero-statement">
              探索生命系统，
              <br className="mobile-break" />
              构建生物制造的未来。
            </h2>
            <p className="hero-description">聚焦合成生物学、生物制造与人工智能的交叉研究。</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#research">
                查看研究 <span aria-hidden="true">→</span>
              </a>
              <a className="button button-outline" href={cvPdf} target="_blank" rel="noreferrer">
                下载 CV <span aria-hidden="true">↗</span>
              </a>
            </div>
            <p className="hero-motto" lang="en">
              “They evolved into us.”
            </p>
          </div>
          <div className="hero-illustration" aria-hidden="true">
            <span className="orbit orbit-one" />
            <span className="orbit orbit-two" />
            <img src={botanical} alt="" width="640" height="640" />
            <span className="illustration-caption" lang="en">
              Life, by design.
            </span>
          </div>
        </section>
        <AcademicBackground />
        <Chat cvUrl={cvPdf} email={email} />
        <section
          id="research"
          className="research-section page-section"
          aria-labelledby="research-title"
        >
          <div className="section-heading">
            <h2 id="research-title">
              研究与探索 <span lang="en">/ Research</span>
            </h2>
            <p>以生物系统为基础，探索可持续的生物制造</p>
          </div>
          <div className="project-grid">
            {projects.map((project, index) => (
              <article className="project-card" key={project.title}>
                <ScienceArt kind={project.kind} />
                <div className="project-copy">
                  <span className="project-number" aria-hidden="true">
                    0{index + 1} /
                  </span>
                  <h3>{project.title}</h3>
                  <p>{project.summary}</p>
                  <span className="short-rule" aria-hidden="true" />
                  <ul className="tag-list" aria-label="研究领域">
                    {project.tags.map((tag) => (
                      <li lang="en" key={tag}>
                        {tag}
                      </li>
                    ))}
                  </ul>
                  <a
                    className="project-question"
                    href={`#ask?${encodeURIComponent(`请介绍一下${project.title}`)}`}
                  >
                    向 AI 助手了解 <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </article>
            ))}
          </div>
          <article className="research-note">
            <span className="note-index" aria-hidden="true">
              03 /
            </span>
            <div>
              <h3 lang="en">Rice genome knockout</h3>
              <p>水稻基因组敲除文库，为功能基因组学研究提供工具。</p>
            </div>
            <a href="https://github.com/alex90thu" target="_blank" rel="noreferrer">
              访问 GitHub <span aria-hidden="true">↗</span>
            </a>
          </article>
        </section>
        <ResearchTools />
        <section
          id="publications"
          className="page-section publications-section"
          aria-labelledby="publications-title"
        >
          <div className="section-heading">
            <h2 id="publications-title">
              代表论文 <span lang="en">/ Publications</span>
            </h2>
            <a className="text-link" href={scholar} target="_blank" rel="noreferrer">
              全部论文 <span aria-hidden="true">↗</span>
            </a>
          </div>
          <div className="publication-list">
            {publications.map((paper) => {
              const url =
                paper.url ||
                `https://scholar.google.com/scholar?q=${encodeURIComponent(paper.title)}`;
              return (
                <article className="publication" key={paper.title}>
                  <span className="publication-year">{paper.year}</span>
                  <div className="publication-copy">
                    <p className="publication-venue" lang="en">
                      {paper.venue}
                    </p>
                    <h3 lang="en">
                      <a href={url} target="_blank" rel="noreferrer">
                        {paper.title}
                      </a>
                    </h3>
                  </div>
                  <a
                    className="paper-link"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${paper.linkLabel ? "在 Scholar 检索论文" : "查看论文"}：${
                      paper.title
                    }`}
                  >
                    <span>{paper.linkLabel || "查看论文"}</span> <span aria-hidden="true">↗</span>
                  </a>
                </article>
              );
            })}
          </div>
        </section>
      </main>
      <Footer email={email} scholar={scholar} faculty={faculty} />
    </div>
  );
}
